import { currentFactValue, type EntityState } from './entities-fold.js';
import { type ViewerId } from './viewer.js';

/**
 * One selectable reveal target — a Viewer plus its human-readable label
 * for the Viewer-Matrix popover / Reveal Composer (`30-design-system.md
 * §7.7`). The GM is never a target (omniscient over canon), so it never
 * appears in this list.
 */
export interface RevealTargetOption {
  viewer: ViewerId;
  label: string;
}

/**
 * Derive the candidate reveal targets for a campaign from its folded
 * entities. Phase-2 increment 1 offers a Character viewer for every
 * `character`-root entity; Party viewers arrive once party membership is
 * modelled (no `party` trait exists in the core pack yet, so inventing one
 * here would be premature). Soft-deleted entities are excluded; the label
 * is the entity's current `name` fact, falling back to its id.
 *
 * Pure + deterministic (sorted by label, case-insensitive) so the picker
 * order is stable across rebuilds.
 */
export function campaignViewers(entities: Record<string, EntityState>): RevealTargetOption[] {
  const out: RevealTargetOption[] = [];
  for (const entity of Object.values(entities)) {
    if (entity.root !== 'character') continue;
    if (entity.soft_deleted) continue;
    const name = currentFactValue(entity.facts.name);
    const label = typeof name === 'string' && name.length > 0 ? name : entity.id;
    out.push({ viewer: { kind: 'character', id: entity.id }, label });
  }
  out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'accent' }));
  return out;
}
